import { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FarmRequiredGuard } from '@/shared/components/FarmRequiredGuard'
import { Brain, Fish, Sparkles, History, CheckCircle, Loader2, Database, BarChart3, Heart, Settings, Clock, Lightbulb, AlertTriangle } from 'lucide-react'
import { pondsApi } from '@/features/ponds/api/pondsApi'
import { queryKeys } from '@/shared/api/queryClient'
import { buildRoute } from '@/shared/lib/constants'
import {
  useAIAnalyses,
  useRootCauseAnalysisStream,
  useFishDiagnosisStream,
  useDeleteAnalysis,
} from '../hooks'
import {
  StreamingResponse,
  RateLimitBadge,
  RootCauseForm,
  FishDiagnosisForm,
  DiagnosisResult,
  RootCauseResult,
  AIHistoryFilters,
  AIHistoryTable,
  AIHistoryCard,
  AIHistoryEmptyState,
  AIHistoryTableSkeleton,
  defaultFilters,
} from '../components'
import type { AIHistoryFilterState } from '../components'
import type { RootCauseAnalysisRequest, FishDiagnosisRequest, Severity } from '../types'

type TabType = 'root-cause' | 'diagnosis' | 'history'

interface AnalysisThinkingUIProps {
  status: string
  statusHistory: string[]
}

function AnalysisThinkingUI({ status, statusHistory }: AnalysisThinkingUIProps) {
  // Determine icon based on status message - matches sidebar icons
  const getIconForStatus = (msg: string) => {
    const lowerMsg = msg.toLowerCase()
    if (lowerMsg.includes('water quality')) return BarChart3  // Same as sidebar
    if (lowerMsg.includes('health')) return Heart             // Same as sidebar
    if (lowerMsg.includes('feed')) return Clock               // Same as sidebar
    if (lowerMsg.includes('maintenance')) return Settings     // Same as sidebar
    if (lowerMsg.includes('deviation') || lowerMsg.includes('risk') || lowerMsg.includes('anomal')) return AlertTriangle
    if (lowerMsg.includes('generating') || lowerMsg.includes('insights')) return Lightbulb
    if (lowerMsg.includes('analyzing') || lowerMsg.includes('total records')) return Brain
    if (lowerMsg.includes('collecting') || lowerMsg.includes('pond data')) return Database
    return Database
  }

  return (
    <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-white to-emerald-50/30 p-6 shadow-sm">
      {/* Header with spinner */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <Brain className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="absolute -top-1 -right-1">
            <Loader2 className="h-5 w-5 text-emerald-600 animate-spin" />
          </div>
        </div>
        <div>
          <p className="font-semibold text-slate-900">AI is thinking...</p>
          <p className="text-sm text-slate-500">
            {status || 'Analyzing your pond data'}
          </p>
        </div>
      </div>

      {/* Real-time status log */}
      <div className="space-y-2">
        {statusHistory.map((msg, index) => {
          const isLatest = index === statusHistory.length - 1
          const StatusIcon = getIconForStatus(msg)

          return (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                isLatest
                  ? 'bg-amber-50 border border-amber-200/50'
                  : 'bg-emerald-50 border border-emerald-200/50'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isLatest
                  ? 'bg-amber-500 text-white'
                  : 'bg-emerald-500 text-white'
              }`}>
                {isLatest ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
              </div>
              <div className="flex items-center gap-2 flex-1">
                <StatusIcon className={`h-4 w-4 flex-shrink-0 ${isLatest ? 'text-amber-600' : 'text-emerald-600'}`} />
                <span className={`text-sm font-medium ${
                  isLatest ? 'text-amber-700' : 'text-emerald-700'
                }`}>
                  {msg}
                </span>
              </div>
            </div>
          )
        })}

        {/* Show placeholder if no history yet */}
        {statusHistory.length === 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200/50">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-white">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
            <span className="text-sm font-medium text-amber-700">
              {status || 'Starting analysis...'}
            </span>
          </div>
        )}
      </div>

      {/* Pulsing indicator */}
      <div className="mt-4 flex justify-center">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  )
}

export function AIInsightsPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Read health check reference params from URL
  const initialHealthCheckId = searchParams.get('healthCheckId') || undefined
  const initialPondId = searchParams.get('pondId') || undefined

  // Determine active tab from URL path
  const activeTab: TabType = location.pathname.endsWith('/diagnosis')
    ? 'diagnosis'
    : location.pathname.endsWith('/history')
    ? 'history'
    : 'root-cause'

  // Handle tab navigation via URL
  const handleTabChange = (tab: TabType) => {
    if (!farmId) return
    switch (tab) {
      case 'root-cause':
        navigate(buildRoute.aiRootCause(farmId))
        break
      case 'diagnosis':
        navigate(buildRoute.aiDiagnosis(farmId))
        break
      case 'history':
        navigate(buildRoute.aiHistory(farmId))
        break
    }
  }

  // Fetch ponds for the farm (use pondsApi.list for consistent cache with other pages)
  const { data: ponds = [] } = useQuery({
    queryKey: queryKeys.ponds.all(farmId || ''),
    queryFn: () => pondsApi.list(farmId!),
    enabled: !!farmId,
  })

  // Filter state for history tab
  const [filters, setFilters] = useState<AIHistoryFilterState>(defaultFilters)

  // Create pondNameMap for display
  const pondNameMap = useMemo(
    () => Object.fromEntries(ponds.map((p) => [p.id, p.name])),
    [ponds]
  )

  // Fetch analysis history - pass filter params to API
  const { data: analysesData, isLoading: isLoadingHistory } = useAIAnalyses(
    farmId || '',
    {
      limit: 50,
      pondId: filters.pondId || undefined,
      analysisType: filters.analysisType || undefined,
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
    }
  )

  // Apply client-side filters (search and severity)
  const filteredAnalyses = useMemo(() => {
    let result = analysesData?.analyses || []

    // Filter by severity
    result = result.filter((a) =>
      filters.severities.includes((a.severity || 'medium') as Severity)
    )

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      result = result.filter(
        (a) =>
          a.identifiedCondition?.toLowerCase().includes(searchLower) ||
          a.summary?.toLowerCase().includes(searchLower) ||
          a.fishSpecies?.toLowerCase().includes(searchLower) ||
          a.createdByName?.toLowerCase().includes(searchLower) ||
          pondNameMap[a.pondId]?.toLowerCase().includes(searchLower)
      )
    }

    return result
  }, [analysesData, filters.severities, filters.search, pondNameMap])

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.pondId ||
    filters.analysisType ||
    filters.severities.length < 4
  )

  // Detect if mobile view for responsive layout
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Root cause streaming hook
  const rootCauseStream = useRootCauseAnalysisStream()

  // Fish diagnosis streaming hook
  const fishDiagnosisStream = useFishDiagnosisStream()

  // Track "preparing results" state for diagnosis
  const [isPreparingDiagnosisResults, setIsPreparingDiagnosisResults] = useState(false)
  const handlePreparingResultsChange = useCallback((isPreparing: boolean) => {
    setIsPreparingDiagnosisResults(isPreparing)
  }, [])

  // Delete mutation
  const deleteMutation = useDeleteAnalysis()

  const handleRootCauseSubmit = (request: RootCauseAnalysisRequest) => {
    if (!farmId) return
    rootCauseStream.analyze(farmId, request)
  }

  const handleFishDiagnosisSubmit = (request: FishDiagnosisRequest) => {
    if (!farmId) return
    setIsPreparingDiagnosisResults(false) // Reset on new submission
    fishDiagnosisStream.diagnose(farmId, request)
  }

  const handleDeleteAnalysis = (analysisId: string) => {
    deleteMutation.mutate(analysisId)
  }

  // Show farm selection guard if no farmId
  if (!farmId) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={false}>
        {null}
      </FarmRequiredGuard>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <Sparkles className="h-6 w-6 text-emerald-600" />
            AI Insights
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            AI-powered analysis and diagnostics for your aquaculture operations
          </p>
        </div>
        <RateLimitBadge />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => handleTabChange('root-cause')}
            className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'root-cause'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <Brain className="h-4 w-4" />
            Root Cause Analyzer
          </button>
          <button
            onClick={() => handleTabChange('diagnosis')}
            className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'diagnosis'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <Fish className="h-4 w-4" />
            Fish Health Diagnosis
          </button>
          <button
            onClick={() => handleTabChange('history')}
            className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <History className="h-4 w-4" />
            Analysis History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Root Cause Analyzer Tab */}
        {activeTab === 'root-cause' && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Form */}
            <div className="rounded-lg border border-slate-200/50 bg-white p-6 shadow-sm">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-medium text-slate-900">
                <Brain className="h-5 w-5 text-emerald-600" />
                Analyze Anomaly
              </h2>
              <RootCauseForm
                farmId={farmId}
                ponds={ponds}
                isStreaming={rootCauseStream.isStreaming}
                onSubmit={handleRootCauseSubmit}
                onCancel={rootCauseStream.abort}
              />
            </div>

            {/* Results */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-slate-900">Analysis Results</h2>

              {/* Loading state during streaming - show thinking steps */}
              {rootCauseStream.isStreaming && (
                <AnalysisThinkingUI
                  status={rootCauseStream.status}
                  statusHistory={rootCauseStream.statusHistory}
                />
              )}

              {/* Error state */}
              {rootCauseStream.error && !rootCauseStream.isStreaming && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-medium text-red-800">Analysis Error</p>
                  <p className="mt-1 text-sm text-red-600">{rootCauseStream.error}</p>
                </div>
              )}

              {/* Completed Result - show nicely formatted root cause analysis */}
              {rootCauseStream.result && !rootCauseStream.isStreaming && (
                <RootCauseResult result={rootCauseStream.result} />
              )}

              {/* Empty State */}
              {!rootCauseStream.isStreaming &&
                !rootCauseStream.content &&
                !rootCauseStream.result && (
                  <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                    <div className="text-center">
                      <Brain className="mx-auto h-8 w-8 text-slate-400" />
                      <p className="mt-2 text-sm text-slate-500">
                        Select a pond and configure the analysis to get started
                      </p>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Fish Diagnosis Tab */}
        {activeTab === 'diagnosis' && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Form */}
            <div className="rounded-lg border border-slate-200/50 bg-white p-6 shadow-sm">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-medium text-slate-900">
                <Fish className="h-5 w-5 text-emerald-600" />
                Diagnose Fish Health
              </h2>
              <FishDiagnosisForm
                ponds={ponds}
                isStreaming={fishDiagnosisStream.isStreaming}
                onSubmit={handleFishDiagnosisSubmit}
                onCancel={fishDiagnosisStream.abort}
                farmId={farmId}
                initialPondId={initialPondId}
                initialHealthCheckId={initialHealthCheckId}
              />
            </div>

            {/* Results */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-slate-900">Diagnosis Results</h2>

              {/* Streaming Response - only show during streaming, not after completion */}
              {fishDiagnosisStream.isStreaming && (
                <StreamingResponse
                  content={fishDiagnosisStream.content}
                  isStreaming={fishDiagnosisStream.isStreaming}
                  status={fishDiagnosisStream.status}
                  error={fishDiagnosisStream.error}
                  onPreparingResultsChange={handlePreparingResultsChange}
                />
              )}

              {/* Preparing Results indicator - shown after reasoning finishes but before results arrive */}
              {fishDiagnosisStream.isStreaming && isPreparingDiagnosisResults && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200/50 shadow-sm">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-purple-600 animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-purple-800">Preparing results...</p>
                    <p className="text-sm text-purple-600">Finalizing diagnosis and recommendations</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                  </div>
                </div>
              )}

              {/* Error state */}
              {fishDiagnosisStream.error && !fishDiagnosisStream.isStreaming && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-medium text-red-800">Analysis Error</p>
                  <p className="mt-1 text-sm text-red-600">{fishDiagnosisStream.error}</p>
                </div>
              )}

              {/* Completed Result - show nicely formatted diagnosis */}
              {fishDiagnosisStream.result && !fishDiagnosisStream.isStreaming && (
                <DiagnosisResult result={fishDiagnosisStream.result} rawContent={fishDiagnosisStream.content} />
              )}

              {/* Empty State */}
              {!fishDiagnosisStream.isStreaming &&
                !fishDiagnosisStream.content &&
                !fishDiagnosisStream.result && (
                  <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                    <div className="text-center">
                      <Fish className="mx-auto h-8 w-8 text-slate-400" />
                      <p className="mt-2 text-sm text-slate-500">
                        Upload photos or describe symptoms to get started
                      </p>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filters */}
            <AIHistoryFilters
              ponds={ponds}
              filters={filters}
              onChange={setFilters}
              resultCount={filteredAnalyses.length}
            />

            {/* Content */}
            {isLoadingHistory ? (
              <AIHistoryTableSkeleton />
            ) : filteredAnalyses.length > 0 ? (
              <>
                {/* Desktop: Table view */}
                {!isMobile && (
                  <AIHistoryTable
                    analyses={filteredAnalyses}
                    pondNameMap={pondNameMap}
                    farmId={farmId}
                    onDelete={handleDeleteAnalysis}
                    isDeleting={deleteMutation.isPending ? deleteMutation.variables as string : undefined}
                  />
                )}

                {/* Mobile: Card view */}
                {isMobile && (
                  <div className="space-y-3">
                    {filteredAnalyses.map((analysis) => (
                      <AIHistoryCard
                        key={analysis.id}
                        analysis={analysis}
                        pondName={pondNameMap[analysis.pondId] || 'Unknown Pond'}
                        farmId={farmId}
                        onDelete={handleDeleteAnalysis}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <AIHistoryEmptyState
                hasFilters={hasActiveFilters}
                onClearFilters={() => setFilters(defaultFilters)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AIInsightsPage
